import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError, EMPTY } from "rxjs";
import { catchError, mergeMap } from "rxjs/operators";
import { Helper } from "../shared/helper";
import { NotifiyService } from "../services/notifier.service";
import { AuthService } from "../services/auth.service";

@Injectable()
export class ResInterceptInterceptor implements HttpInterceptor {
  constructor(
    private helper: Helper,
    private notifier: NotifiyService,
    private authService: AuthService
  ) {}

  private readonly AUTH_ERROR_CODES = [4002, 4004];

  private isAuthFailure(body: any): boolean {
    return (
      body?.success === false &&
      this.AUTH_ERROR_CODES.includes(body?.error_code)
    );
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      mergeMap((event) => {
        if (!(event instanceof HttpResponse)) {
          return [event];
        }

        const body = event.body;

        /* 🔐 AUTH FAILURE */
        if (this.isAuthFailure(body)) {
          this.authService.logout();
          return EMPTY; // ⬅ clean termination
        }

        /* ✅ SUCCESS TOAST */
        if (
          body?.success === true &&
          req.body?.is_show_success_toast !== false
        ) {
          const translated = this.helper.trans.instant(
            "success-code." + body.message
          );

          if (!translated.includes(body.message)) {
            this.notifier.showNotification("success", translated);
          }
        }

        /* ❌ BUSINESS ERROR */
        if (
          body?.success === false &&
          req.body?.is_show_error_toast !== false
        ) {
          const errorKey = body.error_code ?? body.message;
          const translationKey = "error-code." + errorKey;

          const translated = this.helper.trans.instant(translationKey);

          // 1️⃣ Backend-provided message (highest priority)
          if (body.error_message) {
            this.notifier.showNotification("error", body.error_message);
          }

          // 2️⃣ Translation exists
          else if (translated !== translationKey) {
            this.notifier.showNotification("error", translated);
          }

          // 3️⃣ Fallback (always shows something)
          else {
            this.notifier.showNotification(
              "error",
              "Something went wrong. Error code: " + errorKey
            );
          }
        }

        return [event];
      }),

      /* 🌐 REAL HTTP ERRORS */
      catchError((err: HttpErrorResponse) => {
        if (err.status === 0 || err.status >= 500) {
          this.notifier.showNotification("error", "Server unavailable");
        }
        return throwError(() => err);
      })
    );
  }
}
